import React, { useState, useEffect } from 'react';
import { useMediaQuery } from 'react-responsive';
import styled from 'styled-components';
import dayjs from 'dayjs';
import PropTypes from 'prop-types';
import CurrencyPair from './CurrencyPair';
import { CRYPTO_FIAT_PAIRS } from '@/utils/currencies';
import { priceService, getDisplayPrice } from '@/services/priceService';
import Order from './Order';

const StyledBoard = styled.div`
  flex: 1 1 0;
  background: rgba(0, 0, 0, 0.7);
  border-radius: 8px;
  padding: 20px;
  color: white;
  display: flex;
  flex-direction: column;
  justify-content: stretch;
  min-width: 0; // Prevent overflow
  max-width: 880px; // Add a max-width to constrain board size on large screens

  @media (max-width: 1100px) {
    max-width: 100%;
  }
`;

const BoardsContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: stretch;
  gap: clamp(8px, 3vw, 32px);

  & > div:first-child {
    margin-right: 8px;
    margin-left: clamp(32px, 6vw, 80px);
  }
  & > div:last-child {
    margin-right: clamp(32px, 6vw, 80px);
    margin-left: 8px;
  }

  @media (max-width: 900px) {
    flex-direction: column;
    gap: 0;
    align-items: center;
    & > div:first-child,
    & > div:last-child {
      margin: 20px 0;
    }
  }
`;

const Thead = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr auto;
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  font-weight: bold;
  color: #efb348;
  min-width: 0;

  div {
    display: block;
    min-width: 0;
    margin: 0;
    padding: 0;
    text-align: left;
  }

  div:nth-child(4) {
    min-width: 4em; /* Adjust as needed to match Order column */
    /* Optionally, set width: 64px; for stricter control */
  }
`;

const TRow = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr auto;
  gap: 16px;
  padding: 12px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  min-width: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
  }

  div,
  .order-cell {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    min-width: 0;
  }

  div:first-child {
    font-weight: bold;
  }

  .order-cell {
    justify-content: flex-start;
  }
`;

const Time = styled.div`
  text-align: center;
  color: #efb348;
  font-size: 12px;
  margin-top: 10px;
`;

const LoadingText = styled.div`
  text-align: center;
  color: #efb348;
  padding: 40px;
  font-size: 16px;
`;

const PriceList = ({ list = [], rates, isSmallScreen }) => {
  return (
    <div>
      <Thead $isSmallScreen={isSmallScreen}>
        <div>交易对(Pair)</div>
        <div>买价(Ask)</div>
        <div>卖价(Bid)</div>
        <div></div> {/* Empty header for Order column */}
      </Thead>
      <section>
        {list.map(ticker => (
          <TRow key={ticker} $isSmallScreen={isSmallScreen}>
            <div>
              <CurrencyPair ticker={ticker} size={isSmallScreen ? 16 : 20} />
            </div>
            <div>{getDisplayPrice(ticker, 'sell', rates)}</div>
            <div>{getDisplayPrice(ticker, 'buy', rates)}</div>
            <div className="order-cell">
              <Order />
            </div>
          </TRow>
        ))}
      </section>
    </div>
  );
};

PriceList.propTypes = {
  list: PropTypes.array,
  rates: PropTypes.object,
  isSmallScreen: PropTypes.bool,
};

const PriceBoard = () => {
  const isSmallScreen = useMediaQuery({ query: '(max-width: 700px)' });
  const [rates, setRates] = useState({});
  const [updatedAt, setUpdatedAt] = useState(new Date());
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = priceService.subscribe(newRates => {
      setRates(newRates);
      setUpdatedAt(new Date());
      setIsLoading(false);
    });

    priceService.start(30000);

    return () => {
      unsubscribe();
      priceService.stop();
    };
  }, []);

  const formatUpdatedTime = date => {
    return dayjs(date).format('YYYY-MM-DD HH:mm:ss');
  };

  // Split pairs into two arrays of 8
  const firstHalf = CRYPTO_FIAT_PAIRS.slice(0, 8);
  const secondHalf = CRYPTO_FIAT_PAIRS.slice(8, 16);

  if (isLoading) {
    return (
      <StyledBoard $isSmallScreen={isSmallScreen}>
        <LoadingText>正在加载实时汇率...</LoadingText>
      </StyledBoard>
    );
  }

  return (
    <>
      <BoardsContainer>
        <StyledBoard $isSmallScreen={isSmallScreen}>
          <PriceList
            rates={rates}
            list={firstHalf}
            isSmallScreen={isSmallScreen}
          />
        </StyledBoard>
        <StyledBoard $isSmallScreen={isSmallScreen}>
          <PriceList
            rates={rates}
            list={secondHalf}
            isSmallScreen={isSmallScreen}
          />
        </StyledBoard>
      </BoardsContainer>
      <Time>更新时间: {formatUpdatedTime(updatedAt)}</Time>
    </>
  );
};

export default PriceBoard;
